import React, { useRef, useState } from 'react';
import './ActionDropdownMenu.scss';
import { useTranslation } from 'react-i18next';
import { IAlbum } from '../../../../data/models/album';
import { AlbumState } from '../../../../data/models/albumState';
import { IValidationResults } from '../../../../data/models/validation';
import { productApiClient } from '../../../../clients/ProductAPIClient';
import { useSelector } from 'react-redux';
import { ILoadAlbum } from '../../../../state/actions/albumAction';
import store from '../../../../state/store';
import { getAuth } from '../../../../selectors/auth';
import ConfirmationModal from '../../../common/ConfirmationModal/ConfirmationModal';
import useAuthUtilities from '../../../../AuthMiddleware/AuthUtilities';
import { logger } from '../../../../utils/Logger';
import { useNavigate } from 'react-router-dom';
import AddToProjectModal from './AddToProjectModal';
import { ProjectDto } from '../../../../data/models/projectsService';
import RejectionModal from '../../../common/RejectionModal/RejectionModal';
import { ActionMenuItem } from './ActionMenuItem';
import AddToProjectMenuItem from './AddToProjectMenuItem';
import useProductStateTransition from '../../../../hooks/useProductStateTransition';
import DuplicateToDigitalModal from '../../DuplicateForDigitalModal/DuplicateForDigitalModal';
import CreateProduct from '@/components/partials/CreateProduct/CreateProduct';
import { IProductList } from '@/data/models/productList';
import { FormatType } from '@/data/models/formatType';
import {
  merchandiseReleasesGridUrl,
  merchandiseSubmissionsGridUrl,
  releasesGridUrl,
  submissionsGridUrl,
} from '@/components/common/Grid/GridUtilities';
import { ProductViewModes } from '@/utils/productStateUtils';
import {
  getValidationIssuesFromError,
  getValidationIssuesFromPayload,
  isValidationError,
} from '@/utils/validationErrors';
import { changeRequestApiClient } from '@/clients/ChangeRequestAPIClient';
import _ from 'lodash';
import { IChangeRequest } from '@/data/models/changeRequest';
import SuccessToast from '@/components/common/SuccessToast/SuccessToast';

interface Props {
  album: IAlbum;
  project?: ProjectDto;
  onProjectAdded: () => void;
  onProjectRemoved: () => void;
  setErrorBanner: (errorBanner: boolean) => void;
  setErrors: (errors: IValidationResults[]) => void;
  formatType: FormatType;
  productViewMode?: ProductViewModes;
  changeRequest?: IChangeRequest;
  hasChangeRequest?: boolean;
  readonly?: boolean;
}

export enum ActionMenuActions {
  SubmitForReview,
  SubmitForImport,
  Reject,
  ReturnToDraft,
  ReturnToReview,
  DuplicateProduct,
  DeleteDraft,
  AddToProject,
  RemoveFromProject,
  DuplicateForDigital,
  EditProduct,
  RequestChange,
  ChangePending,
  SubmitChangeRequest,
  DeleteChangeRequest,
}

const ActionDropdownMenu = ({
  album,
  project,
  onProjectAdded,
  onProjectRemoved,
  setErrorBanner,
  setErrors,
  formatType,
  productViewMode,
  changeRequest,
  hasChangeRequest,
  readonly,
}: Props) => {
  const { t } = useTranslation();
  const { hasWriteAccess } = useSelector(getAuth);
  const loadAlbumStore: ILoadAlbum = store.getState().loadAlbumState;
  const [visible, setVisible] = useState(false);
  const show = () => setVisible(true);
  const hide = () => setVisible(false);
  const [showDuplicateToDigitalModal, setShowDuplicateToDigitalModal] =
    useState(false);
  const [showAddToProjectModal, setShowAddToProjectModal] = useState(false);
  const [showProjectAddedToast, setShowProjectAddedToast] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showDuplicateProductModal, setShowDuplicateProductModal] =
    useState(false);
  const toggleDuplicateProductModal = () => {
    setShowDuplicateProductModal(!showDuplicateProductModal);
  };
  const [duplicateProductData, setDuplicateProductData] =
    useState<IProductList>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalBody, setConfirmationModalBody] = useState('');
  const [confirmationModalHeader, setConfirmationModalHeader] = useState('');
  const isChangeRequest = productViewMode === ProductViewModes.ChangeRequest;
  const [confirmationModalAction, setConfirmationModalAction] = useState(
    () => () => {
      return Promise.resolve();
    }
  );
  const [
    isSubmitForImportValidationInProgress,
    setIsSubmitForImportValidationInProgress,
  ] = useState(false);
  const submitForImportValidationInFlight = useRef(false);
  const {
    canUserEditProduct,
    canUserSeeReturnToDraftButton,
    canUserSeeReturnToUnderReviewButton,
    canUserSeeRejectButton,
    canUserSeeSubmitForImportButton,
    isInternalUser,
  } = useAuthUtilities();
  const navigate = useNavigate();
  const { updateProductState } = useProductStateTransition(
    formatType === FormatType.Merchandise
      ? merchandiseSubmissionsGridUrl
      : submissionsGridUrl
  );
  const { updateChangeRequestState } = useProductStateTransition(
    formatType === FormatType.Merchandise
      ? merchandiseReleasesGridUrl
      : releasesGridUrl
  );

  const showConfirmModal = (
    headerText: string,
    bodyText: string,
    confirmAction: () => () => Promise<void>
  ) => {
    setConfirmationModalHeader(headerText);
    setConfirmationModalBody(bodyText);
    setConfirmationModalAction(confirmAction);
    setShowConfirmationModal(true);
  };

  const showChangeRequestButtons = () => {
    return (
      !isChangeRequest &&
      album.albumState === AlbumState.Released &&
      isInternalUser()
    );
  };

  const showEditProductMode = () => {
    return readonly && canUserEditProduct(album.albumState);
  };

  const editProductMode = () => {
    if (formatType === FormatType.Merchandise) {
      navigate(`/Merchandise/EditProduct/${album.id}`);
    } else {
      navigate(`/Physical/EditProduct/Album/${album.id}`);
    }
  };

  const createChangeRequest = async () => {
    const confirmAction = () => async () => {
      await changeRequestApiClient
        .createChangeRequest(album.id!)
        .then(() => {
          if (formatType === FormatType.Merchandise) {
            navigate(`/Merchandise/ChangeRequest/${album.id}`);
          } else {
            navigate(`/Physical/ChangeRequest/Album/${album.id}`);
          }
        })
        .catch((reason) => {
          console.error('changeRequest creation failed', reason);
        });
    };
    showConfirmModal(
      t('product.changeRequest.confirmationModalTitle'),
      t('product.changeRequest.confirmationModalBody'),
      confirmAction
    );
  };

  const editChangeRequest = async () => {
    if (formatType === FormatType.Merchandise) {
      navigate(`/Merchandise/ChangeRequest/${album.id}`);
      return;
    }
    navigate(`/Physical/ChangeRequest/Album/${album.id}`);
  };

  const returnChangeRequestToDraft = async () => {
    const confirmAction = () => async () => {
      if (changeRequest) {
        await updateChangeRequestState({
          albumId: changeRequest.albumId,
          changeRequestId: changeRequest.id!,
          albumTitle: changeRequest.changeRequestAlbum.title,
          newState: AlbumState.Draft,
          nonce: changeRequest.nonce!,
          returnToState: true,
        });
      }
    };

    showConfirmModal(
      t('product.editProduct.returnToDraftModalHeader'),
      t('product.editProduct.returnToDraftModalBody'),
      confirmAction
    );
  };

  const deleteChangeRequest = async () => {
    if (!changeRequest) return;
    const confirmAction = () => async () => {
      await changeRequestApiClient
        .deleteChangeRequest(
          changeRequest.albumId,
          changeRequest.id!,
          changeRequest.nonce
        )
        .then(() => {
          if (formatType === FormatType.Merchandise) {
            navigate(`/Merchandise/ProductDetails/${album.id}`);
          } else {
            navigate(`/Physical/ProductDetails/Album/${album.id}`);
          }
        })
        .catch((reason) => {
          console.error('changeRequest deletion failed', reason);
        });
    };
    showConfirmModal(
      t('product.changeRequest.deleteChangesTitle'),
      t('product.changeRequest.deleteChangesBody'),
      confirmAction
    );
  };

  const validateAndSubmitStateChange = async (newState: AlbumState) => {
    const includeSubmitValidations =
      newState === AlbumState.PendingImport &&
      album.albumState !== AlbumState.PendingImport;
    if (includeSubmitValidations && submitForImportValidationInFlight.current) {
      return;
    }

    if (includeSubmitValidations) {
      submitForImportValidationInFlight.current = true;
      setIsSubmitForImportValidationInProgress(true);
    }

    const albumJson = JSON.stringify(album);
    try {
      const changeRequestValidationId =
        changeRequest?.id ?? album.activeChangeRequestId;
      const response = await productApiClient.validateAlbum(
        albumJson,
        includeSubmitValidations,
        changeRequestValidationId
      );
      const validationIssues = getValidationIssuesFromPayload(response);
      const validationErrors = validationIssues.filter(isValidationError);

      if (validationErrors.length === 0) {
        setErrors(validationIssues);
        setErrorBanner(validationIssues.length > 0);
        const confirmAction = () => async () => {
          try {
            if (changeRequest) {
              await updateChangeRequestState({
                albumId: changeRequest.albumId!,
                changeRequestId: changeRequest.id!,
                albumTitle: changeRequest.changeRequestAlbum.title!,
                newState: AlbumState.PendingImport,
                nonce: changeRequest.nonce!,
              });
            } else {
              await updateProductState({
                albumId: album.id!,
                albumTitle: album.title,
                newState,
              });
            }
          } catch (error: unknown) {
            const validationIssues = getValidationIssuesFromError(error);
            if (validationIssues.length > 0) {
              setErrors(validationIssues);
              setErrorBanner(true);
              return;
            }

            logger.error(
              error instanceof Error ? error.message : String(error)
            );
            throw error;
          }
        };

        if (newState == AlbumState.UnderReview) {
          showConfirmModal(
            t('product.editProduct.submitForReviewModalHeader'),
            t('product.editProduct.submitForReviewModalBody'),
            confirmAction
          );
        } else if (newState == AlbumState.PendingImport) {
          showConfirmModal(
            t('product.editProduct.submitForImportModalHeader'),
            t('product.editProduct.submitForImportModalBody'),
            confirmAction
          );
        } else if (newState == AlbumState.Released) {
          showConfirmModal(
            t('product.editProduct.releaseProductModalHeader'),
            t('product.editProduct.releaseProductModalBody'),
            confirmAction
          );
        }
      } else {
        setErrors(validationIssues);
        setErrorBanner(true);
      }
    } catch (error: any) {
      logger.error(error);
    } finally {
      if (includeSubmitValidations) {
        submitForImportValidationInFlight.current = false;
        setIsSubmitForImportValidationInProgress(false);
      }
    }
  };

  const handleReturnToState = async (newState: AlbumState) => {
    if (
      album.albumState === AlbumState.Released &&
      newState === AlbumState.PendingImport
    ) {
      return;
    }

    const confirmAction = () => async () => {
          await updateProductState({
            albumId: album.id!,
            albumTitle: album.title,
            newState,
            returnToState: true,
          });
    };
    if (newState == AlbumState.Draft) {
      showConfirmModal(
        t('product.editProduct.returnToDraftModalHeader'),
        t('product.editProduct.returnToDraftModalBody'),
        confirmAction
      );
    }
    if (newState == AlbumState.UnderReview) {
      showConfirmModal(
        t('product.editProduct.returnToUnderReviewModalHeader'),
        t('product.editProduct.returnToUnderReviewModalBody'),
        confirmAction
      );
    }
  };

  const handleRejection = async () => {
    setShowRejectionModal(true);
  };

  const handleDuplicateProduct = () => {
    setShowDuplicateProductModal(true);
    setDuplicateProductData({
      id: parseInt(album.id!),
      gtin: album.gtin,
      title: album.title,
      displayArtist: album.displayArtist,
      labelName: album.label.name,
      format: album.format,
      albumState: AlbumState.Draft,
      releaseDate: album.releaseDate,
      priceCode: album.albumMetaData?.pricing?.priceCode ?? '',
      catalogNumber: album?.catalogNumber,
      trackCount: album.tracks?.length ?? 0,
    } as IProductList);
  };

  const handleDeleteDraft = () => {
    showConfirmModal(
      t('product.productList.deleteDraft'),
      t('product.productList.deleteDraftWarning'),
      () => async () => {
        await productApiClient.deleteAlbum(album.id!);
        navigate(
          formatType === FormatType.Merchandise
            ? merchandiseSubmissionsGridUrl
            : submissionsGridUrl
        );
      }
    );
  };

  const handleDuplicateForDigital = () => {
    if (!album.id) {
      return;
    }
    setShowDuplicateToDigitalModal(true);
  };

  const handleAddToProject = () => {
    setShowAddToProjectModal(true);
  };

  const handleProjectAdded = () => {
    onProjectAdded();
    setShowProjectAddedToast(true);
  };

  const handleRemoveFromProject = () => {
    onProjectRemoved();
    hide();
  };

  return (
    <div className="action-dropdown-container">
      {showProjectAddedToast ? (
        <SuccessToast
          message={t('project_service.product_added_success')}
          onClose={() => setShowProjectAddedToast(false)}
        />
      ) : null}
      <ConfirmationModal
        showModal={showConfirmationModal}
        setShowModal={setShowConfirmationModal}
        message={confirmationModalBody}
        header={confirmationModalHeader}
        handleConfirm={confirmationModalAction}
      ></ConfirmationModal>
      {showAddToProjectModal ? (
        <AddToProjectModal
          showModal={showAddToProjectModal}
          setShowModal={setShowAddToProjectModal}
          album={album}
          onProjectAdded={handleProjectAdded}
        ></AddToProjectModal>
      ) : null}
      {showRejectionModal ? (
        <RejectionModal
          albumId={album.id!}
          albumTitle={album.title}
          showModal={showRejectionModal}
          formatType={formatType}
          setShowModal={setShowRejectionModal}
        ></RejectionModal>
      ) : null}
      {showDuplicateToDigitalModal && formatType !== FormatType.Merchandise ? (
        <DuplicateToDigitalModal
          show={showDuplicateToDigitalModal}
          onClose={() => setShowDuplicateToDigitalModal(false)}
          albumId={album.id!}
          albumState={album.albumState}
        />
      ) : null}
      {showDuplicateProductModal ? (
        <CreateProduct
          toggle={true}
          togglePopUp={toggleDuplicateProductModal}
          albumToCopy={duplicateProductData}
          formatType={formatType}
        />
      ) : null}
      {hasWriteAccess && (
        <div className="btn-group">
          <button
            type="button"
            className="btn btn-secondary action-menu-button"
          >
            {t('productActions.dropdown.actions')}
          </button>
          <button
            type="button"
            className="btn btn-secondary action-dropdown-button"
            onClick={visible ? hide : show}
            onBlur={hide}
          >
            <i className="fa fa-angle-down"></i>
          </button>
          {visible ? (
            <ul
              className="action-dropdown-menu"
              role="menu"
              onMouseDown={(e) => e.preventDefault()}
            >
              {!isChangeRequest && (
                <>
                  {showChangeRequestButtons() ? (
                    <>
                      <ActionMenuItem
                        action={ActionMenuActions.RequestChange}
                        disabled={!_.isNil(album.activeChangeRequestId)}
                        onClickHandler={() => createChangeRequest()}
                      ></ActionMenuItem>
                      <ActionMenuItem
                        action={ActionMenuActions.ChangePending}
                        disabled={_.isNil(album.activeChangeRequestId)}
                        onClickHandler={() => editChangeRequest()}
                      ></ActionMenuItem>
                      <li>
                        <hr className="action-dropdown-divider" />
                      </li>
                    </>
                  ) : null}

                  {showEditProductMode() && (
                    <>
                      <ActionMenuItem
                        action={ActionMenuActions.EditProduct}
                        onClickHandler={() => editProductMode()}
                      ></ActionMenuItem>
                      <li>
                        <hr className="action-dropdown-divider" />
                      </li>
                    </>
                  )}

                  <ActionMenuItem
                    action={ActionMenuActions.SubmitForReview}
                    disabled={
                      !(
                        (album.albumState === AlbumState.Draft ||
                          album.albumState === AlbumState.Rejected) &&
                        !loadAlbumStore.isJustCreated
                      )
                    }
                    onClickHandler={() =>
                      validateAndSubmitStateChange(AlbumState.UnderReview)
                    }
                  ></ActionMenuItem>

                  {canUserSeeSubmitForImportButton() ? (
                    <ActionMenuItem
                      action={ActionMenuActions.SubmitForImport}
                      disabled={
                        album.albumState !== AlbumState.UnderReview ||
                        isSubmitForImportValidationInProgress
                      }
                      onClickHandler={() =>
                        validateAndSubmitStateChange(AlbumState.PendingImport)
                      }
                    ></ActionMenuItem>
                  ) : null}

                  {canUserSeeRejectButton() ? (
                    <ActionMenuItem
                      action={ActionMenuActions.Reject}
                      disabled={album.albumState !== AlbumState.UnderReview}
                      onClickHandler={() => handleRejection()}
                    ></ActionMenuItem>
                  ) : null}

                  {canUserSeeReturnToDraftButton() ? (
                    <ActionMenuItem
                      action={ActionMenuActions.ReturnToDraft}
                      disabled={
                        album.albumState !== AlbumState.UnderReview &&
                        album.albumState !== AlbumState.Error
                      }
                      onClickHandler={() =>
                        handleReturnToState(AlbumState.Draft)
                      }
                    ></ActionMenuItem>
                  ) : null}

                  {canUserSeeReturnToUnderReviewButton() ? (
                    <ActionMenuItem
                      action={ActionMenuActions.ReturnToReview}
                      disabled={album.albumState !== AlbumState.PendingImport}
                      onClickHandler={() =>
                        handleReturnToState(AlbumState.UnderReview)
                      }
                    ></ActionMenuItem>
                  ) : null}
                  <li>
                    <hr className="action-dropdown-divider" />
                  </li>
                  <ActionMenuItem
                    action={ActionMenuActions.DuplicateProduct}
                    onClickHandler={() => handleDuplicateProduct()}
                  ></ActionMenuItem>
                  <ActionMenuItem
                    action={ActionMenuActions.DeleteDraft}
                    disabled={
                      !(
                        album.albumState === AlbumState.Draft ||
                        album.albumState === AlbumState.Rejected
                      )
                    }
                    onClickHandler={() => handleDeleteDraft()}
                  ></ActionMenuItem>

                  <li>
                    <hr className="action-dropdown-divider" />
                  </li>

                  <ActionMenuItem
                    action={ActionMenuActions.DuplicateForDigital}
                    disabled={false}
                    onClickHandler={handleDuplicateForDigital}
                  />

                  <AddToProjectMenuItem
                    project={project}
                    onAddToProject={handleAddToProject}
                    onRemoveFromProject={handleRemoveFromProject}
                  ></AddToProjectMenuItem>
                </>
              )}
              {isChangeRequest ? (
                <>
                  <ActionMenuItem
                    action={ActionMenuActions.SubmitChangeRequest}
                    disabled={
                      changeRequest?.state === AlbumState.Error ||
                      !hasChangeRequest
                    }
                    onClickHandler={() =>
                      validateAndSubmitStateChange(AlbumState.PendingImport)
                    }
                  ></ActionMenuItem>
                  <ActionMenuItem
                    action={ActionMenuActions.ReturnToDraft}
                    disabled={changeRequest?.state !== AlbumState.Error}
                    onClickHandler={() => returnChangeRequestToDraft()}
                  ></ActionMenuItem>
                  <li>
                    <hr className="action-dropdown-divider" />
                  </li>
                  <ActionMenuItem
                    action={ActionMenuActions.DeleteChangeRequest}
                    onClickHandler={() => deleteChangeRequest()}
                  ></ActionMenuItem>
                </>
              ) : null}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ActionDropdownMenu;
